import React, { useState, useEffect, useRef, useCallback } from "react";
import rgbHex from "rgb-hex";
import * as UTILS from "../../../utils/Utils";
import { cloneDeep } from "lodash";
import { track } from "@amplitude/analytics-browser";
import useLastClickedElement from "../../../hooks/useLastClickedElement";
import BundledEditor from "../Text/BundleEditor";
import tableUtils, { detectStyleChanges, distributeColumnsIcon, distributeRowsIcon, getListFormatStyles, getBgColorTableAttribute, getElementStyles, getParentContainer, getResizerContainer, WHITE_COLOR, applyTableAlignment, emojiToUnicode, toHexColor } from "./tableUtils";
import { mainStyleFormats, useTinyStyleFormats } from "../Text/useTinyStyleFormats";


function TinyEditor(props) {
  const tableWrapperId = "table-wrapper-" + props.ID;
  const tableColumnSticky = props.element.desktopStyle.tableColumnSticky;
  const tableHeaderSticky = props.element.desktopStyle.tableHeaderSticky;
  const justifyContent = props.element.desktopStyle.justifyContent || 'flex-start';
  const [key, setKey] = useState(0);
  const [isResizerContainer, setIsResizerContainer] = useState(undefined);
  const { formats } = useTinyStyleFormats(props);

  const lastClicked = useLastClickedElement();
  const editorRef = useRef(null);
  const tableRefInitialValues = {
    isActive: false,
    isOpenWindow: false,
    isContextmenu: false,
  }
  const tableRef = useRef(tableRefInitialValues);
  const previousStylesRef = useRef({
    table: {},
    rows: {},
    cells: {},
    initialized: false,
  });


  const getElementId = (element, index) => {
    return element.id || `${element.tagName}-${index}`; // Usa ID si existe, o genera uno basado en el Ã­ndice
  };
  const getSelectedElement = (editor, type) => {
    const selectedNode = editor.selection.getNode();
    if (type === 'row') {
      return selectedNode.closest('tr');
    }
    if (type === 'cell') {
      return selectedNode.closest('td, th');
    }
    return null;
  };
  const captureInitialStyles = (table) => {
    const rows = table.querySelectorAll('tr');
    const cells = table.querySelectorAll('td');
    const rowStyles = {};
    const cellStyles = {};

    rows.forEach((row, index) => {
      const rowId = getElementId(row, index);
      rowStyles[rowId] = getElementStyles(row);
    });

    cells.forEach((cell, index) => {
      const cellId = getElementId(cell, index);
      cellStyles[cellId] = getElementStyles(cell, true);
    });

    previousStylesRef.current = {
      table: {},
      rows: rowStyles,
      cells: cellStyles,
      initialized: true,
    };
  };

  useEffect(() => {
    dragElement(document.getElementById("tinyToolbar" + props.ID));

    function dragElement(elmnt) {
      var pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;
      if (document.getElementById(elmnt.id)) {
        // if present, the header is where you move the DIV from:
        document.getElementById(elmnt.id).onmousedown = dragMouseDown;
      } else {
        // otherwise, move the DIV from anywhere inside the DIV:
        elmnt.onmousedown = dragMouseDown;
      }

      function dragMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();
        e = e || window.event;

        // get the mouse cursor position at startup:
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        // call a function whenever the cursor moves:
        document.onmousemove = elementDrag;
      }

      function elementDrag(e) {
        e.preventDefault();
        e.stopPropagation();
        e = e || window.event;

        // calculate the new cursor position:
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        // set the element's new position:
        elmnt.style.top = elmnt.offsetTop - pos2 + "px";
        elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
      }

      function closeDragElement() {
        // stop moving when mouse button is released:
        document.onmouseup = null;
        document.onmousemove = null;
      }
    }
  }, []);

  const isResizerContainerClicked = useCallback(() => {
    let clickedElement = lastClicked.current;
    return clickedElement && clickedElement.getAttribute('data-editor-id') === props.ID;
  }, [lastClicked, props.ID]);

  const handleBlur = useCallback((event) => {
    if (isResizerContainerClicked()) return;
    const editorContent = editorRef.current.getContent();
    const content = editorContent
      .replace(/border-collapse:\s*collapse;/gi, 'border-collapse: separate;')
      .replace(/border-spacing:\s*[^;]+;/gi, 'border-spacing: 0px !important;');

    let finalContent = /border-spacing:\s*0px;/.test(content)
      ? content
      : content.replace(/(<table[^>]*style="[^"]*)/gi, '$1 border-spacing: 0px !important;');

    finalContent = applyTableAlignment(finalContent, justifyContent);

    const encodedHTMLContent = emojiToUnicode(finalContent);
    let layout = cloneDeep(props.layout);
    const newLayout = UTILS.UpdateLayout(
      layout,
      props.ID,
      (item) => {
        item.desktopStyle.content = encodedHTMLContent;
      },
      (colItem) => {
        colItem.desktopStyle.content = encodedHTMLContent;
      },
      (colItemNested) => {
        colItemNested.desktopStyle.content = encodedHTMLContent;
      }
    );
    props.layoutArray(props.layoutTitle, newLayout);
    setIsResizerContainer(false);

  }, [props.layout, props.layoutArray, props.layoutTitle, props.ID, isResizerContainerClicked]);

  // Handle transparent background colors for sticky columns and headers
  useEffect(() => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    const table = editor.dom.select('table')[0];

    if (!table) return;

    if (tableColumnSticky) {
      // Get all first column cells (td elements in the first column)
      const firstColumnCells = editor.dom.select('table tr td:first-child');
      firstColumnCells.forEach(cell => {
        const backgroundColor = editor.dom.getStyle(cell, 'background-color');
        // Check if the cell background is transparent
        const isTransparent = backgroundColor === 'transparent' ||
          backgroundColor === 'rgba(0, 0, 0, 0)' ||
          backgroundColor === 'rgba(0,0,0,0)' ||
          backgroundColor === '';

        // If the cell is transparent, apply white background using the existing style pattern
        if (isTransparent) {
          const existingStyle = cell.getAttribute('style') || '';
          const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
          const newStyle = `${cleanedStyle} background-color: ${WHITE_COLOR};`.trim();
          cell.setAttribute('style', newStyle);
          cell.setAttribute('data-mce-style', newStyle);
          // Notify TinyMCE of the DOM change
          editor.nodeChanged();
        }
      });
    }

    if (tableHeaderSticky) {
      // Get all header row cells (td elements in the first row)
      const headerRowCells = editor.dom.select('table tr:first-child td');
      headerRowCells.forEach(cell => {
        const backgroundColor = editor.dom.getStyle(cell, 'background-color');
        // Check if the cell background is transparent
        const isTransparent = backgroundColor === 'transparent' ||
          backgroundColor === 'rgba(0, 0, 0, 0)' ||
          backgroundColor === 'rgba(0,0,0,0)' ||
          backgroundColor === '';

        // If the cell is transparent, apply white background using the existing style pattern
        if (isTransparent) {
          const existingStyle = cell.getAttribute('style') || '';
          const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
          const newStyle = `${cleanedStyle} background-color: ${WHITE_COLOR};`.trim();
          cell.setAttribute('style', newStyle);
          cell.setAttribute('data-mce-style', newStyle);
          // Notify TinyMCE of the DOM change
          editor.nodeChanged();
        }
      });
    }

  }, [tableColumnSticky, tableHeaderSticky, editorRef.current]);

  const updateTableHeight = (height) => {
    const editorBody = editorRef.current?.getBody();
    if (editorBody) {
      const table = editorBody.querySelector('table');
      if (table) {
        table.style.height = height ? height : '';
      }
    }
  }
  const handleGlobalClick = (event) => {
    // Detects if the click is inside the table even if user clics on resizers
    const editorBody = editorRef.current?.getBody();
    if (!editorBody) return;
    setTimeout(() => {
      const tableIsActive = editorBody.classList.contains('mce-edit-focus')
      const parentContainer = getParentContainer(props.ID);
      const clickInside = parentContainer && parentContainer.contains(event.target);
      const resizersContainer = getResizerContainer(props.ID);
      const { isContextmenu, isOpenWindow } = tableRef.current;

      if (clickInside && tableIsActive) {
        if (resizersContainer) {
          resizersContainer.className = 'show-resizer';
        }
        // only case when show resizers
        if (isOpenWindow && resizersContainer) {
          resizersContainer.className = 'hidden-resizer';
        } else if (!isContextmenu && !isOpenWindow && resizersContainer) {
          resizersContainer.className = 'show-resizer';
        } else if (isContextmenu && !isOpenWindow) {
          tableRef.current.isOpenWindow = false;
          tableRef.current.isContextmenu = false;
        } else {
          tableRef.current.isOpenWindow = false;
          tableRef.current.isContextmenu = false;
        }

        tableRef.current.isActive = true;
      } else {
        if (resizersContainer) {
          resizersContainer.className = 'show-resizer';
        }
        if (isResizerContainerClicked()) {
          // If the click is on Resizers, keep the resizers visible
        } else {
          // If the click is outside the table, hide the resizers
          updateTableHeight();
          tableRef.current.isActive = false;
          if (resizersContainer) {
            resizersContainer.className = 'hidden-resizer';
          }
        }
      }
    }, 10);
  };

  const handleMouseMove = (e) => {
    // hide/show resizers based on mouse position
    if (tableRef.current.isActive) {
      const resizersContainer = getResizerContainer(props.ID);
      const parentContainer = getParentContainer(props.ID);
      if (!parentContainer) return;
      const rect = parentContainer.getBoundingClientRect();
      const offset = 2;
      // Check if the mouse is inside the parent container
      const isInside =
        e.clientX >= rect.left - offset &&
        e.clientX <= rect.right + offset &&
        e.clientY >= rect.top - offset &&
        e.clientY <= rect.bottom + offset;
      if (resizersContainer) {
        resizersContainer.className = (isInside
          && !tableRef.current.isContextmenu
          && !tableRef.current.isOpenWindow) ? 'show-resizer' : 'hidden-resizer';
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener('mousedown', handleGlobalClick);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, []);

  useEffect(() => {
    // re render table
    setKey(key + 1);
    tableRef.current = tableRefInitialValues;
  }, [
    props.preset,
    tableHeaderSticky,
    tableColumnSticky,
    props.layout,
    justifyContent,
  ]);

  const handleInit = useCallback((evt, editor) => {
    // set init fn for editor
    editorRef.current = editor;

    // INITIALIZE OVERFLOW X
    const table = editor.dom.select('table')[0];
    const initialContainer = document.getElementById(tableWrapperId);
    if (!table || !initialContainer) {
      return;
    }
    const computedStyle = window.getComputedStyle(table);
    const initialTableWidth = parseInt(computedStyle.width) || 0;
    const initialContainerWidth = initialContainer.offsetWidth || 0;
    if (initialTableWidth > initialContainerWidth) {
      initialContainer.style.overflowX = 'auto';
    } else {
      initialContainer.style.overflowX = 'hidden';
    }

    // END INITIALIZE OVERFLOW X


    //config editor window / popups manager options
    const originalOpen = editor.windowManager.open;
    editor.windowManager.open = (config, params) => {
      // hide / show properties in table properties dialog
      if (config.title === 'Row Properties' || config.title === 'Cell Properties' || config.title === 'Table Properties') {
        if (config.body && config.body.type === 'tabpanel' && config.body.tabs) {
          const updatedTabs = config.body.tabs.map((tab, index) => {
            if (index === 0 && tab.name === 'general') { // "General" Tab : hide Row / Cell Type input
              const hiddenItems = ['Row type', 'Cell type', 'Caption', 'Scope', 'Alignment', 'Cell spacing'];
              let tabs = Array.isArray(tab?.items) ? tab.items : [];

              // Hide properties in Table Properties dialog
              if (config?.title === 'Table Properties' && Array.isArray(tabs) && tabs.length > 0 && tabs[0]?.items) {
                tabs = tabs[0].items;
                hiddenItems.push('Height')
              }
              const updatedItems = tabs.filter((item) => !hiddenItems.includes(item.label));
              return {
                ...tab,
                items: updatedItems,
              };
            }

            if (tab.name === 'advanced') { // "General" Tab : hide Row / Cell Type input
              const updatedItems = tab.items
                .filter((item) => item.label !== 'Border style')

              return {
                ...tab,
                items: updatedItems,
              };
            }
            return tab;
          });
          const newConfig = {
            ...config,
            body: {
              ...config.body,
              tabs: updatedTabs,
            },
          };
          return originalOpen.call(editor.windowManager, newConfig, params);
        }
      }
      return originalOpen.call(editor.windowManager, config, params);
    };

  }, []);

  return (
    <>
      <div
        className="tinyMCE-Table-Draggable-Toolbar-Container"
        id={"tinyToolbar" + props.ID}
      ></div>
      <BundledEditor
        key={key}
        onBlur={handleBlur}
        isResizerContainer={isResizerContainer}
        initialValue={props.desktopContent}
        onInit={(evt, editor) => handleInit(evt, editor)}
        init={{
          promotion: false,
          inline: true,
          selector: `#${props.ID}`,
          menubar: "",
          plugins:
            "table advtable lists advlist link powerpaste emoticons charmap code",
          placeholder: "Click here to build table...",
          font_size_input_default_unit: "px",
          font_size_formats:
            "8px 9px 10px 11px 12px 13px 14px 15px 16px 17px 18px 19px 20px 22px 24px 26px 28px 30px 32px 36px 40px 44px 48px 52px 56px 60px 64px 68px 72px",
          toolbar: [
            "table undo redo customInsertButton styles fontfamily  fontsizeinput bold italic underline",
            "  backcolor forecolor align lineheight  link  bullist numlist outdent indent | subscript superscript emoticons charmap code |tabledelete  tableprops tablecellprops tablerowprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol  advtablesort advtablerownumbering distribute-cols distribute-rows ",
          ],
          powerpaste_allow_local_images: true,
          powerpaste_word_import: "prompt",
          powerpaste_html_import: "prompt",
          powerpaste_googledocs_import: "prompt",
          table_resize_bars: true,
          object_resizing: true,
          table_toolbar: "",
          table_tab_navigation: true,
          // toolbar_persist: true,
          paste_block_drop: true,

          fixed_toolbar_container: "#" + "tinyToolbar" + props.ID,
          link_list: props.pagesList,
          link_default_target: "_blank",
          link_default_protocol: "http",
          table_default_styles: tableUtils.table_default_styles,
          table_default_attributes: tableUtils.table_default_attributes,
          content_style: (() => {
            const wrapperId = `#table-wrapper-${props.ID}`;

            // Base table styles
            const baseTableStyles = `
              ${wrapperId} table { 
                border-collapse: separate !important; 
                border-spacing: 0 !important;
              }
            `;

            // Case 1: No sticky (both false)
            if (!tableHeaderSticky && !tableColumnSticky) {
              return baseTableStyles;
            }

            // Case 2: Only header sticky
            if (tableHeaderSticky && !tableColumnSticky) {
              return `
                ${baseTableStyles}
                ${wrapperId} table tr:first-child {
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                }
                ${wrapperId} table tr:first-child td {
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                }
              `;
            }

            // Case 3: Only column sticky
            if (!tableHeaderSticky && tableColumnSticky) {
              return `
                ${baseTableStyles}
                ${wrapperId} table tr td:first-child {
                  position: sticky !important;
                  left: 0 !important;
                  z-index: 5 !important;
                  box-shadow: 2px 0 4px -2px rgba(0,0,0,0.1) !important;
                }
                /* Handle merged cells in first column - ensure they stay above other columns */
                ${wrapperId} table tr td:first-child[rowspan] {
                  z-index: 6 !important;
                }
                /* Ensure all cells in first column have proper z-index for merged scenarios */
                ${wrapperId} table tr:nth-child(n+1) td:first-child {
                  z-index: 5 !important;
                }
                /* For merged cells, ensure subsequent rows don't interfere */
                ${wrapperId} table tr td:first-child[rowspan] + tr td:first-child {
                  z-index: 4 !important;
                }
                /* Additional rule to ensure merged cells maintain z-index over adjacent columns */
                ${wrapperId} table tr td:first-child[rowspan] {
                  position: sticky !important;
                  left: 0 !important;
                  z-index: 6 !important;
                }
              `;
            }

            // Case 4: Both header and column sticky
            if (tableHeaderSticky && tableColumnSticky) {
              return `
                ${baseTableStyles}
                ${wrapperId} table tr:first-child {
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                }
                ${wrapperId} table tr:first-child td {
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                }
                ${wrapperId} table tr td:first-child {
                  position: sticky !important;
                  left: 0 !important;
                  z-index: 5 !important;
                  box-shadow: 2px 0 4px -2px rgba(0,0,0,0.1) !important;
                }
                ${wrapperId} table tr:first-child td:first-child {
                  position: sticky !important;
                  top: 0 !important;
                  left: 0 !important;
                  z-index: 15 !important;
                  box-shadow: 2px 0 4px -2px rgba(0,0,0,0.1) !important;
                }
                /* Handle merged cells in first column when both sticky - ensure they stay above other columns */
                ${wrapperId} table tr td:first-child[rowspan] {
                  z-index: 6 !important;
                }
                /* Ensure proper z-index for merged cells in header row */
                ${wrapperId} table tr:first-child td:first-child[rowspan] {
                  z-index: 16 !important;
                }
                /* For merged cells, ensure subsequent rows don't interfere */
                ${wrapperId} table tr td:first-child[rowspan] + tr td:first-child {
                  z-index: 4 !important;
                }
                /* Additional rule to ensure merged cells maintain z-index over adjacent columns */
                ${wrapperId} table tr td:first-child[rowspan] {
                  position: sticky !important;
                  left: 0 !important;
                  z-index: 6 !important;
                }
                /* Special handling for merged cells in header row when both sticky */
                ${wrapperId} table tr:first-child td:first-child[rowspan] {
                  position: sticky !important;
                  top: 0 !important;
                  left: 0 !important;
                  z-index: 16 !important;
                }
              `;
            }

            // Default fallback
            return baseTableStyles;
          })(),

          line_height_formats:
            "1.1 1.2 1.25 1.3 1.4 1.4 1.4 1.4 1.4 1.4 1.4 1.5 2",
          // newline_behavior:"linebreak",
          color_map: props.preset
            ? (() => {
              let thing = cloneDeep(props.preset);
              return thing.reduce((acc, curr, index) => {
                acc.push(curr.replace(/#\b/g, ""));
                // Insert the element only if it's not the last element

                acc.push("");

                return acc;
              }, []);
            })()
            : ["ffffff", "black", "white", "00000"],
          cleanup: true,
          font_family_formats: UTILS.googleFontsEditor,
          statusbar: true,
          elementpath: true,
          forced_root_block: "div",
          forced_root_block_attrs: {
            style: `font-family:${props.selectedFont}; font-size:14px;`,
          },
          browser_spellcheck: true,
          link_default_protocol: "http",

          formats: formats,
          style_formats: tableUtils.style_formats,

          setup: (editor) => {
            let pivotRow = null;
            let lastSelectedNode = null;
            let windowOpen = null;
            let originalFormat = null;
            let originalNode = null;
            let lastFormatBlockApplied = null;
            let isListApplied = false;

            editor.on('NodeChange', (e) => {
              // Save the current row where the cursor is located
              const selectedNode = editor.selection.getNode();

              lastSelectedNode = selectedNode;
              pivotRow = selectedNode.closest('td');
            });

            editor.on('BeforeExecCommand', function (e) {
              if (!originalFormat) {
                const formats = editor.formatter.get();
                const appliedFormats = [];
                // Check which style formats are applied at the current selection
                Object.keys(formats).forEach((formatName) => {
                  if (editor.formatter.match(formatName)) {
                    appliedFormats.push(formatName);
                  }
                });
                originalFormat = appliedFormats.find(format => mainStyleFormats.includes(format));
              }
              if (e.command === 'InsertUnorderedList' || e.command === 'InsertOrderedList') {
                if (!isListApplied) {
                  const originalElement = editor.selection.getNode();
                  originalNode = originalElement.cloneNode(true);
                  isListApplied = true;
                }
              }
            });

            editor.on("OpenWindow", () => {
              const elements = document.getElementsByClassName(
                "ephox-snooker-resizer-bar"
              );
              [...elements].map((element) => {
                element.style.display = "none";
              });
            });

            editor.on('TableModified', (e) => {
              const { table, structure, style } = e;
              const { isActive, isContextmenu, isOpenWindow } = tableRef;
              // START STRUCTURE CHANGES
              const computedStyle = window.getComputedStyle(table);
              const tableWidth = parseInt(computedStyle.width) || 0;
              const container = document.getElementById(tableWrapperId);
              if (tableWidth > container.offsetWidth) {
                container.style.overflowX = 'auto';
              } else {
                container.style.overflowX = 'hidden';
              }
              if (isActive && !isContextmenu && !isOpenWindow) {
                const resizersContainer = getResizerContainer(props.ID);
                resizersContainer.className = 'show-resizer';
              }
              // END STRUCTURE CHANGES


              // STYLE CHANGES
              if (style) {
                const editor = editorRef.current;
                const currentTableStyles = getElementStyles(table);

                //Inferred modified row from selection
                // if row is modified, update all td background color in the row modified
                const selectedRow = getSelectedElement(editor, 'row');
                if (selectedRow && windowOpen === 'mceTableRowProps') {
                  const rowId = getElementId(selectedRow, Array.from(table.querySelectorAll('tr')).indexOf(selectedRow));
                  const currentRowStyles = getElementStyles(selectedRow);
                  const rowChanges = detectStyleChanges(previousStylesRef.current.rows[rowId] || {}, currentRowStyles);
                  previousStylesRef.current.rows[rowId] = currentRowStyles;
                  setTimeout(() => {
                    const selectedNode = editor.selection.getNode();
                    const currentTr = selectedNode.closest('tr');

                    if (currentTr) {
                      // Si cambió backgroundColor, actualiza todos los <td>
                      if (rowChanges?.backgroundColor) {
                        const trBackgroundColor = currentTr.style.backgroundColor;
                        const allTds = currentTr.querySelectorAll('td');

                        allTds.forEach(td => {
                          const existingStyle = td.getAttribute('style') || '';
                          const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
                          const newStyle = `${cleanedStyle} background-color: ${trBackgroundColor};`.trim();
                          td.setAttribute('style', newStyle);
                          td.setAttribute('data-mce-style', newStyle);
                        });
                      }

                      if (rowChanges?.border?.newValue) {
                        const borderColor = rowChanges.border.newValue.match(/rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)/)[0];
                        const allTds = currentTr.querySelectorAll('td');

                        allTds.forEach(td => {
                          const existingStyle = td.getAttribute('style') || '';
                          const cleanedStyle = existingStyle.replace(/border-color\s*:\s*[^;]+;?/i, '').trim();
                          const newStyle = `${cleanedStyle} border-color: ${borderColor};`.trim();
                          td.setAttribute('style', newStyle);
                          td.setAttribute('data-mce-style', newStyle);
                        });
                      }
                    }
                  }, 0);
                }
                const selectedCell = getSelectedElement(editor, 'cell');
                if (selectedCell && windowOpen === 'mceTableCellProps') {
                  const cellId = getElementId(selectedCell, Array.from(table.querySelectorAll('td')).indexOf(selectedCell));
                  const currentCellStyles = getElementStyles(selectedCell, true);

                  const cellChanges = detectStyleChanges(previousStylesRef.current.cells[cellId] || {}, currentCellStyles);

                  const isWidthChanged = cellChanges?.width?.oldValue !== cellChanges?.width?.newValue;

                  // Actualizar estilos de la celda
                  previousStylesRef.current.cells[cellId] = currentCellStyles;
                  setTimeout(() => {
                    const selectedNode = editor.selection.getNode();
                    const currentTd = selectedNode.closest('td');
                    if (currentTd) {
                      if (isWidthChanged) {

                        const width = currentTd.style.width;

                        const colIndex = currentTd.cellIndex;
                        const table = currentTd.closest('table');
                        const rows = table.querySelectorAll('tr');

                        rows.forEach(row => {
                          const cell = row.cells[colIndex];
                          if (cell) {
                            cell.style.width = width;
                          }
                        });
                      }
                      if (cellChanges.border) {
                        const table = currentTd.closest('table');
                        table.style.borderCollapse = 'separate';
                      }
                    }
                  }, 0);
                }
                if (windowOpen === 'mceTableProps') {
                  const selectedNode = editor.selection.getNode();
                  const currentTable = selectedNode.closest('table');

                  if (currentTable) {
                    const currBackgroundColor = toHexColor(currentTableStyles?.backgroundColorStyle);
                    const previousBackgroundColor = toHexColor(previousStylesRef.current.table?.backgroundColor);

                    if (currBackgroundColor !== previousBackgroundColor) {
                      const tableBackgroundColor = currBackgroundColor;
                      const allTds = currentTable.querySelectorAll('td');
                      const allTrs = currentTable.querySelectorAll('tr');


                      allTds.forEach(td => {
                        const existingStyle = td.getAttribute('style') || '';
                        const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
                        const newStyle = tableBackgroundColor
                          ? `${cleanedStyle} background-color: ${tableBackgroundColor};`.trim()
                          : cleanedStyle;
                        td.setAttribute('style', newStyle);
                        td.setAttribute('data-mce-style', newStyle);
                      });

                      allTrs.forEach(tr => {
                        const existingStyle = tr.getAttribute('style') || '';
                        const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
                        const newStyle = tableBackgroundColor
                          ? `${cleanedStyle} background-color: ${tableBackgroundColor};`.trim()
                          : cleanedStyle;
                        tr.setAttribute('style', newStyle);
                        tr.setAttribute('data-mce-style', newStyle);
                      });
                    }
                  }
                }
                captureInitialStyles(table);
              }
            });

            editor.on('ExecCommand', (e) => {
              const selectedNode = editor.selection.getNode();

              if (e.command === 'mceTableRowProps') {
                updateTableHeight();
                windowOpen = 'mceTableRowProps';
              }
              if (e.command === 'mceTableCellProps') {
                windowOpen = 'mceTableCellProps';
              }
              if (e.command === 'mceTableProps') {
                windowOpen = 'mceTableProps';
              }
              if (e.command === 'mceTableInsertRowAfter' || e.command === 'mceTableInsertRowBefore') {
                if (pivotRow) {
                  const currentTr = pivotRow.closest('tr');
                  const newTr = selectedNode.closest('tr');
                  const trBackgroundColor = currentTr.style.backgroundColor;

                  if (trBackgroundColor) {
                    // Applied color to all td in the new row for consistency
                    const allTds = newTr.querySelectorAll('td');
                    allTds.forEach(td => {
                      const existingStyle = td.getAttribute('style') || '';
                      const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
                      td.setAttribute('style', `${cleanedStyle} background-color: ${trBackgroundColor};`);
                      td.setAttribute('data-mce-style', `${cleanedStyle} background-color: ${trBackgroundColor};`);
                    });

                    // Apply background color to the new row itself
                    const existingTrStyle = newTr.getAttribute('style') || '';
                    const cleanedTrStyle = existingTrStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
                    newTr.setAttribute('style', `${cleanedTrStyle} background-color: ${trBackgroundColor};`);
                    newTr.setAttribute('data-mce-style', `${cleanedTrStyle} background-color: ${trBackgroundColor};`);
                  }
                }
              }
              if (e.command === 'mceToggleFormat') {
                const formatApplied = e.value; // e.g., 'h1', 'paragraph', 'none'
                const format = formats[formatApplied];

                if (format && format.styles) {
                  lastFormatBlockApplied = format?.block;
                  editor.formatter.apply(format?.block);
                }

                // apply format to lists 
                const range = editor.selection.getRng();
                const fragment = range.cloneContents(); // Clona los nodos seleccionados
                const lis = Array.from(fragment.querySelectorAll('li'));
                // single list item
                const cssText = getListFormatStyles(format);
                if (selectedNode.parentElement.tagName === 'LI') {
                  const li = selectedNode.parentElement;
                  li.setAttribute('style', cssText);
                  li.setAttribute('data-mce-style', cssText);
                }
                // multiple list items
                if (lis.length > 0) {
                  lis.forEach(li => {
                    li.setAttribute('style', cssText);
                    li.setAttribute('data-mce-style', cssText);
                  });

                  range.deleteContents();
                  range.insertNode(fragment);
                  const current = editor.selection.getNode();
                  const parentList = current.closest('ul,ol');
                  if (parentList) {
                    parentList.querySelectorAll('li').forEach(li => {
                      if (!li.textContent.trim()) {
                        li.remove();
                      }
                    });
                  }

                  editor.nodeChanged();
                  editor.selection.collapse(false);
                }
              }

              // LIST COMMANDS
              if (e.command === 'InsertUnorderedList' || e.command === 'InsertOrderedList') {
                const format = formats[lastFormatBlockApplied || originalFormat];
                const cssText = getListFormatStyles(format);
                const lis = Array.from(selectedNode.querySelectorAll('li'));

                if (lis.length > 0) {
                  lis.forEach(li => {
                    li.setAttribute('style', cssText);
                    li.setAttribute('data-mce-style', cssText);
                  });
                }

                editor.formatter.apply(lastFormatBlockApplied || originalFormat || 'p');
              }
            });
            editor.on("OpenWindow", () => {
              // Use a Map to store removed classes with elements as keys
              let removedClassesMap = new Map();

              function removeDragHandleClasses() {
                const elements = document.querySelectorAll(
                  '[class*="dragHandle"]'
                );
                elements.forEach((element) => {
                  // Store removed classes for this element
                  const removedClasses = [];
                  element.classList.forEach((className) => {
                    if (className.includes("dragHandle")) {
                      removedClasses.push(className);
                      element.classList.remove(className);
                    }
                  });
                  // Store the removed classes in the map
                  if (removedClasses.length > 0) {
                    removedClassesMap.set(element, removedClasses);
                  }
                });
              }

              // Remove drag handle classes on mount
              removeDragHandleClasses();

              // Restore drag handle classes on cleanup
              editor.on("CloseWindow", () => {
                function restoreDragHandleClasses() {
                  removedClassesMap.forEach((removedClasses, element) => {
                    removedClasses.forEach((className) => {
                      element.classList.add(className);
                    });
                  });
                  // Clear the map after restoring
                  removedClassesMap.clear();
                }
                restoreDragHandleClasses();
              });
            });

            editor.on("focus", (e) => {
              setIsResizerContainer(true);
              if (
                document
                  .getElementById(tableWrapperId)
                  .getBoundingClientRect().x <
                window.innerWidth / 2
              ) {
                document.getElementById("tinyToolbar" + props.ID).style.right =
                  "inherit";
                document.getElementById("tinyToolbar" + props.ID).style.left =
                  "0px";
              } else {
                document.getElementById("tinyToolbar" + props.ID).style.left =
                  "inherit";
                document.getElementById("tinyToolbar" + props.ID).style.right =
                  "0px";
              }
              if (
                document
                  .getElementById(tableWrapperId)
                  .getBoundingClientRect().y < 162
              ) {
                //   -document.getElementById(props.ID).getBoundingClientRect().y
                // );
                document.getElementById("tinyToolbar" + props.ID).style.top =
                  -document
                    .getElementById(tableWrapperId)
                    .getBoundingClientRect().y + "px";
              } else {
                document.getElementById("tinyToolbar" + props.ID).style.top =
                  "-125px";
              }
            });

            editor.on("init", (e) => {
              document.addEventListener("touchmove", function (event) { }, {
                passive: true,
              });
              // Apply custom styles to mce-content-body
              const contentBody = editor.getBody();
              if (contentBody) {
                contentBody.style.display = 'flex';
                contentBody.style.justifyContent = justifyContent || 'flex-start';
              }
            });

            editor.on("dragover", function () {
              tinymce
                .get()
                .forEach((instance) => instance.mode.set("readonly"));
            });

            // add custom icons for toolbar plug in
            editor.ui.registry.addIcon('distribute-horizontal', distributeColumnsIcon);
            editor.ui.registry.addIcon('distribute-vertical', distributeRowsIcon);
            editor.ui.registry.addButton('distribute-cols', {
              tooltip: 'Distribute Columns',
              icon: 'distribute-horizontal',
              onAction: () => {
                const table = editor.dom.getParent(editor.selection.getNode(), 'table');
                if (table) {
                  // Get the rows
                  const rows = table.rows;

                  // get columns count
                  let numColumns = 0;
                  const firstRowCells = rows[0].cells;
                  for (let cell of firstRowCells) {
                    numColumns += cell.colSpan || 1;
                  }

                  for (let row of rows) {
                    for (let cell of row.cells) {
                      const cellSpan = cell.colSpan || 1;
                      const adjustedWidth = `${(100 / numColumns) * cellSpan}%`;
                      editor.dom.setStyle(cell, 'width', adjustedWidth);
                    }
                  }
                } else {
                  alert('Please select a table.');
                }
              },
              onSetup: (buttonApi) => {
                // Enable/disable the button depending on whether a table is selected
                const editorEventCallback = (eventApi) => {
                  const table = editor.dom.getParent(eventApi.element, 'table');
                  buttonApi.setEnabled(!!table);
                };
                editor.on('NodeChange', editorEventCallback);
                return () => editor.off('NodeChange', editorEventCallback); // Clean up the event
              }
            });

            // Add the button to distribute rows evenly, omitting the first row
            editor.ui.registry.addButton('distribute-rows', {
              tooltip: 'Distribute Rows',
              icon: 'distribute-vertical',
              onAction: () => {
                const table = editor.dom.getParent(editor.selection.getNode(), 'table');
                if (table) {
                  const rows = table.rows;
                  if (rows.length > 1) {
                    // Calculate currentTableHeight by summing heights of all rows except the header
                    let currentTableHeight = 0;
                    for (let i = 1; i < rows.length; i++) {
                      currentTableHeight += rows[i].offsetHeight;
                    }
                    // Fix the table height to the sum of non-header rows
                    editor.dom.setStyle(table, 'height', currentTableHeight + 'px');

                    const remainingHeight = currentTableHeight;
                    const numRowsToDistribute = rows.length - 1;
                    const rowHeight = remainingHeight / numRowsToDistribute;

                    // Set height for each row after the header
                    for (let i = 1; i < rows.length; i++) {
                      editor.dom.setStyle(rows[i], 'height', rowHeight + 'px');
                    }
                  }
                }
              },
              onSetup: (buttonApi) => {
                // Enable/disable the button depending on whether a table is selected
                const editorEventCallback = (eventApi) => {
                  const table = editor.dom.getParent(eventApi.element, 'table');
                  buttonApi.setEnabled(!!table);
                };
                editor.on('NodeChange', editorEventCallback);
                return () => editor.off('NodeChange', editorEventCallback);  // Clean up the event
              }
            });

            editor.ui.registry.addButton("customInsertButton", {
              tooltip: "Clear Formatting",
              icon: "remove-formatting",
              onAction: function (e) {
                const selectedNode = editor.selection.getNode();
                if (selectedNode.tagName === 'UL' || selectedNode.tagName === 'OL') {
                  editor.execCommand("RemoveList");
                  isListApplied = false;
                  lastFormatBlockApplied = null;
                  originalFormat = null;
                }
                tinymce.activeEditor.formatter.apply("noneForRemoveFormat");
                editor.execCommand("removeformat");
              },
            });
            editor.on("FormatApply", (e) => {
              e.format === "alignleft" ||
                e.format === "alignright" ||
                e.format == "aligncenter" ||
                e.format == "alignjustify"
                ? track("Table Text Alignment Changed", {
                  alignment: e.format,
                })
                : "";
            });

            editor.on("OpenWindow", (e) => {
              tableRef.current.isOpenWindow = true;
              const dialog = e.dialog;

              // Disable scrolling when OpenWindow is on
              const gridLinesDiv = document.querySelector('.gridLines');
              if (gridLinesDiv) {
                gridLinesDiv.style.overflow = 'hidden'; // Hide scrollbar and disable scrolling
              }

              // set default colors
              if (dialog && dialog.getData()) {
                const dialogData = dialog.getData();
                console.log('dialogData', dialogData);
                const isTableProperties = dialogData.hasOwnProperty('width') && dialogData.hasOwnProperty('cellpadding') && dialogData.hasOwnProperty('cellspacing'); // Table Properties has width, height, and cellspacing
                const isCellProperties = dialogData.hasOwnProperty('backgroundcolor') && !dialogData.hasOwnProperty('height') && !dialogData.hasOwnProperty('cellspacing'); // Cell Properties has backgroundcolor but not height or cellspacing
                const isRowProperties = dialogData.hasOwnProperty('height') && !dialogData.hasOwnProperty('cellspacing'); // Row Properties has height but not cellspacing

                if (isTableProperties) {
                  // Handle Table Properties
                  const table = editor.dom.select('table')[0];
                  if (table) {
                    const bgColor = getBgColorTableAttribute(table);
                    const backgroundColor = bgColor || '';

                    previousStylesRef.current.table = { ...previousStylesRef.current.table, backgroundColor };
                    dialog.setData({
                      ...dialogData,
                      backgroundcolor: backgroundColor,
                    });
                  }
                } else if (isCellProperties) {
                  const td = lastSelectedNode.closest('td');
                  const bgColor = getBgColorTableAttribute(td);

                  const backgroundColor = !bgColor
                    ? ''
                    : bgColor === WHITE_COLOR
                      ? ''
                      : bgColor;

                  dialog.setData({
                    ...dialogData,
                    width: dialogData.width || '25%',
                    backgroundcolor: backgroundColor,
                  });
                } else if (isRowProperties && lastSelectedNode) {
                  let rowBackgroundColor = WHITE_COLOR;
                  const tr = lastSelectedNode.closest('tr');

                  if (tr) {
                    const backgroundColor = getBgColorTableAttribute(tr);
                    const table = tr.closest('table');
                    if (table) {
                      const allRows = table.querySelectorAll('tr');
                      const isFirstRow = allRows[0] === tr;
                      rowBackgroundColor = isFirstRow
                        ? '#455B73'
                        : backgroundColor
                          ? backgroundColor
                          : WHITE_COLOR;
                    }
                  }

                  dialog.setData({
                    ...dialogData,
                    backgroundcolor: rowBackgroundColor
                  });
                }
              }
              document.getElementById("tinyToolbar" + props.ID).onmousedown =
                null;
              document.getElementById("tinyToolbar" + props.ID).onmouseup =
                null;
              document.getElementById("tinyToolbar" + props.ID).onmousemove =
                null;
            });
            editor.on('contextmenu', (e) => {
              tableRef.current.isContextmenu = true;
            });
            editor.on("CloseWindow", () => {
              tableRef.current.isOpenWindow = false;
              tableRef.current.isContextmenu = false;

              // Enable scrolling when OpenWindow is off
              const gridLinesDiv = document.querySelector('.gridLines');
              if (gridLinesDiv) {
                gridLinesDiv.style.overflow = 'hidden auto'; // Restore original overflow value
              }

              dragElement(document.getElementById("tinyToolbar" + props.ID));

              function dragElement(elmnt) {
                var pos1 = 0,
                  pos2 = 0,
                  pos3 = 0,
                  pos4 = 0;
                if (document.getElementById(elmnt.id)) {
                  // if present, the header is where you move the DIV from:
                  document.getElementById(elmnt.id).onmousedown = dragMouseDown;
                } else {
                  // otherwise, move the DIV from anywhere inside the DIV:
                  elmnt.onmousedown = dragMouseDown;
                }

                function dragMouseDown(e) {
                  e.preventDefault();
                  e.stopPropagation();
                  e = e || window.event;

                  // get the mouse cursor position at startup:
                  pos3 = e.clientX;
                  pos4 = e.clientY;
                  document.onmouseup = closeDragElement;
                  // call a function whenever the cursor moves:
                  document.onmousemove = elementDrag;
                }

                function elementDrag(e) {
                  e.preventDefault();
                  e.stopPropagation();
                  e = e || window.event;

                  // calculate the new cursor position:
                  pos1 = pos3 - e.clientX;
                  pos2 = pos4 - e.clientY;
                  pos3 = e.clientX;
                  pos4 = e.clientY;
                  // set the element's new position:
                  elmnt.style.top = elmnt.offsetTop - pos2 + "px";
                  elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
                }

                function closeDragElement() {
                  // stop moving when mouse button is released:
                  document.onmouseup = null;
                  document.onmousemove = null;
                }
              }
            });
          },
        }
        }
      />
    </>
  );
}

export default React.memo(TinyEditor);